// Competency (Phase 8, §20). Tabs: Staff Competency (20.2) + Company Accreditations (20.1).
import { useState } from "react";
import { Award, ShieldCheck } from "lucide-react";
import PeopleRegister from "@/components/people/PeopleRegister";
import { useAuth } from "@/context/AuthContext";

const INTERNAL = new Set(["director", "administrator", "consultant"]);
const COMPANY_EDIT = new Set(["director", "administrator"]);

const STAFF_FIELDS = [
    { key: "person_name", label: "Person", required: true, half: true },
    { key: "person_role", label: "Role", half: true },
    { key: "competency_type", label: "Type", type: "select", optionsKey: "competency_types", half: true },
    { key: "status", label: "Status", type: "select", optionsKey: "statuses", half: true },
    { key: "title", label: "Qualification / item", required: true },
    { key: "body", label: "Awarding body / provider", half: true },
    { key: "reference", label: "Reference / number", half: true },
    { key: "date_obtained", label: "Date obtained", type: "date", half: true },
    { key: "expiry_date", label: "Expiry date", type: "date", half: true },
    { key: "reminder_date", label: "Renewal reminder", type: "date", half: true },
    { key: "notes", label: "Notes", type: "textarea" },
];
const STAFF_COLUMNS = [
    { key: "person_name", label: "Person", search: (r) => `${r.person_name} ${r.person_role || ""}`, render: (r) => <div><div className="font-semibold text-[#1F1F1F]">{r.person_name}</div><div className="text-[11px]" style={{ color: "#9A968F" }}>{r.person_role || ""}</div></div> },
    { key: "title", label: "Competency", search: (r) => `${r.title} ${r.body || ""}`, render: (r) => <div><div>{r.title}</div><div className="text-[11px]" style={{ color: "#9A968F" }}>{r.body || ""}</div></div> },
    { key: "competency_type", label: "Type" },
    { key: "expiry_date", label: "Expiry", render: (r) => r.expiry_date || "—" },
];

const COMPANY_FIELDS = [
    { key: "accreditation_type", label: "Type", type: "select", optionsKey: "accreditation_types", half: true },
    { key: "status", label: "Status", type: "select", optionsKey: "statuses", half: true },
    { key: "name", label: "Name", required: true },
    { key: "provider", label: "Insurer / certifying body", half: true },
    { key: "reference", label: "Policy / certificate no.", half: true },
    { key: "coverage_amount", label: "Coverage amount", half: true },
    { key: "start_date", label: "Start date", type: "date", half: true },
    { key: "expiry_date", label: "Expiry / renewal date", type: "date", half: true },
    { key: "reminder_date", label: "Renewal reminder", type: "date", half: true },
    { key: "notes", label: "Notes", type: "textarea" },
];
const COMPANY_COLUMNS = [
    { key: "name", label: "Accreditation", search: (r) => `${r.name} ${r.provider || ""} ${r.reference || ""}`, render: (r) => <div><div className="font-semibold text-[#1F1F1F]">{r.name}</div><div className="text-[11px]" style={{ color: "#9A968F" }}>{r.provider || ""}{r.reference ? ` · ${r.reference}` : ""}</div></div> },
    { key: "accreditation_type", label: "Type" },
    { key: "coverage_amount", label: "Cover", render: (r) => r.coverage_amount || "—" },
    { key: "expiry_date", label: "Renewal", render: (r) => r.expiry_date || "—" },
];

export default function CompetencyPage() {
    const { user } = useAuth();
    const showCompany = user && INTERNAL.has(user.role);
    const [tab, setTab] = useState("staff");

    return (
        <div data-testid="competency-root" className="space-y-4">
            {showCompany && (
                <div className="flex gap-1 bg-[#F0EDE8] p-1 rounded-[10px] w-fit">
                    <button data-testid="competency-tab-staff" onClick={() => setTab("staff")}
                        className={`px-4 py-1.5 rounded-[8px] text-[12.5px] font-semibold inline-flex items-center gap-1.5 ${tab === "staff" ? "bg-white shadow-sm text-[#1F1F1F]" : "text-[#6F6A64]"}`}>
                        <Award className="w-3.5 h-3.5" /> Staff competency
                    </button>
                    <button data-testid="competency-tab-company" onClick={() => setTab("company")}
                        className={`px-4 py-1.5 rounded-[8px] text-[12.5px] font-semibold inline-flex items-center gap-1.5 ${tab === "company" ? "bg-white shadow-sm text-[#1F1F1F]" : "text-[#6F6A64]"}`}>
                        <ShieldCheck className="w-3.5 h-3.5" /> Company accreditations
                    </button>
                </div>
            )}

            {tab === "staff" ? (
                <PeopleRegister
                    testid="competency" title="Staff Competency" subtitle="qualifications, CPD, DBS, licences & memberships"
                    endpoint="/competency" icon={Award} fields={STAFF_FIELDS} columns={STAFF_COLUMNS}
                    fileLabel="Evidence" scoped showClientField
                />
            ) : (
                <PeopleRegister
                    testid="company" title="Company Compliance" subtitle="Priority Risk insurances & accreditations"
                    endpoint="/company-compliance" icon={ShieldCheck} fields={COMPANY_FIELDS} columns={COMPANY_COLUMNS}
                    fileLabel="Evidence" scoped={false} showClientField={false} editRoles={COMPANY_EDIT}
                />
            )}
        </div>
    );
}
